import type { RootProps } from '@vuepic/vue-datepicker';
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { ko, enUS } from 'date-fns/locale';
import { useMyInfoStore } from '@/config/stores/my-info';
import { storeToRefs } from 'pinia';
import { getTzTimeNow } from '@/common/utils/time.utils';

export const useCalendar = (
  disabledSelectedBeforeDay?: string,
  {
    disabledToday = true,
    allowFutureDates = false,
  }: {
    disabledToday?: boolean;
    allowFutureDates?: boolean;
  } = {},
) => {
  const { myInfo } = storeToRefs(useMyInfoStore());

  const userLanguage = computed<RootProps['locale']>(() => {
    const { language } = myInfo.value;

    switch (language) {
      default:
      case 'KO':
        return ko;
      case 'EN':
        return enUS;
    }
  });

  const monthYearButtonDirection = computed(() => {
    return userLanguage.value?.code === 'ko' ? 'row-reverse' : 'row';
  });

  const today = getTzTimeNow();

  const makers = ref<RootProps['markers']>([
    {
      date: today.toDate(),
      type: 'dot',
    },
  ]);

  const checkDisabledDate = (date: Date): boolean => {
    const isBeforeDisabledDate = disabledSelectedBeforeDay
      ? dayjs(date).startOf('day').isBefore(dayjs(disabledSelectedBeforeDay))
      : false;

    if (allowFutureDates) {
      return isBeforeDisabledDate;
    }

    const todayOrNextDay = disabledToday ? today : today.add(1, 'day');

    return (
      isBeforeDisabledDate || dayjs(date).startOf('day').isAfter(todayOrNextDay.startOf('day'))
    );
  };

  const isToday = (date: Date): boolean => {
    const givenDate = dayjs(date).tz();

    return givenDate.isSame(today, 'day');
  };

  const isSunday = (date: Date): boolean => {
    const day = dayjs(date).tz().day();
    const SUNDAY = 0;

    return day === SUNDAY;
  };

  const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const getFormattedDay = (index: number) => DAYS[index];

  return {
    userLanguage,
    monthYearButtonDirection,
    makers,
    checkDisabledDate,
    today,
    isToday,
    isSunday,
    getFormattedDay,
  };
};
